import React, { useState } from 'react';
import { Calculator, ExternalLink, Maximize2, Minimize2, RefreshCw, Info, Percent, CalendarDays, Coins, ArrowRightLeft } from 'lucide-react';
import CalculadoraTAE from './CalculadoraTAE';
import CalculadoraIntereses from './CalculadoraIntereses';
import CalculadoraPlazos from './CalculadoraPlazos';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Calculadora = () => {
  const [activeTab, setActiveTab] = useState('plazos');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoadingPlazos, setIsLoadingPlazos] = useState(true);
  const [isLoadingIntereses, setIsLoadingIntereses] = useState(true);
  const [pesetas, setPesetas] = useState('');
  const [euros, setEuros] = useState('');
  const TIPO_CAMBIO = 166.386;

  const convertirPesetasAEuros = (pts) => {
    if (!pts || isNaN(pts)) return '';
    return (parseFloat(pts) / TIPO_CAMBIO).toFixed(2);
  };

  const convertirEurosAPesetas = (eur) => {
    if (!eur || isNaN(eur)) return '';
    return Math.round(parseFloat(eur) * TIPO_CAMBIO);
  };

  const handlePesetasChange = (value) => {
    setPesetas(value);
    setEuros(convertirPesetasAEuros(value));
  };

  const handleEurosChange = (value) => {
    setEuros(value);
    setPesetas(convertirEurosAPesetas(value));
  };

  const limpiarConversor = () => {
    setPesetas('');
    setEuros('');
  };

  const urlPlazos = 'https://www.calculo-plazosprocesales.com/';
  const urlIntereses = 'https://www.calculo-intereses.com/';

  const handleIframePlazosLoad = () => {
    setIsLoadingPlazos(false);
  };

  const handleIframeInteresesLoad = () => {
    setIsLoadingIntereses(false);
  };

  const refreshIframe = () => {
    if (activeTab === 'plazos') {
      setIsLoadingPlazos(true);
      const iframe = document.getElementById('calculadora-plazos-iframe');
      if (iframe) {
        iframe.src = iframe.src;
      }
    } else {
      setIsLoadingIntereses(true);
      const iframe = document.getElementById('calculadora-intereses-iframe');
      if (iframe) {
        iframe.src = iframe.src;
      }
    }
  };

  const openInNewTab = () => {
    const url = activeTab === 'plazos' ? urlPlazos : urlIntereses;
    window.open(url, '_blank');
  };

  return (
    <div className={`p-6 space-y-4 ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-blue-600" />
            Calculadoras
          </h1>
          <p className="text-gray-600 mt-1">
            Herramientas de cálculo para plazos procesales e intereses legales
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={refreshIframe}
            title="Recargar"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={openInNewTab}
            title="Abrir en nueva pestaña"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-4 mb-4">
          <TabsTrigger value="plazos" className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />
            Calculadora de Plazos
          </TabsTrigger>
          <TabsTrigger value="intereses" className="flex items-center gap-2">
            <Percent className="w-4 h-4" />
            Calculadora de Intereses
          </TabsTrigger>
          <TabsTrigger value="tae" className="flex items-center gap-2" data-testid="tab-tae">
            <Percent className="w-4 h-4" />
            Calculadora TAE
          </TabsTrigger>
          <TabsTrigger value="pesetas" className="flex items-center gap-2">
            <Coins className="w-4 h-4" />
            Pesetas / Euros
          </TabsTrigger>
        </TabsList>

        {/* TAB: Calculadora TAE */}
        <TabsContent value="tae" className="space-y-4">
          <CalculadoraTAE />
        </TabsContent>

        {/* TAB: Calculadora de Plazos */}
        <TabsContent value="plazos" className="space-y-4">
          <CalculadoraPlazos />
        </TabsContent>

        {/* TAB: Calculadora de Intereses */}
        <TabsContent value="intereses" className="space-y-4">
          <CalculadoraIntereses />
        </TabsContent>
      
        {/* TAB: Conversor Pesetas/Euros */}
        <TabsContent value="pesetas" className="space-y-4">
          {!isFullscreen && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-amber-900 font-medium">Conversor oficial Pesetas - Euros</p>
                    <p className="text-amber-700 mt-1">
                      Tipo de cambio fijo establecido el 1 de enero de 1999: 1 EUR = 166,386 PTS
                    </p>
                  </div>
                  <Badge variant="outline" className="text-amber-700 border-amber-300 flex-shrink-0">
                    Tipo fijo oficial
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-6">
              <div className="max-w-xl mx-auto space-y-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-3">
                    <Coins className="w-8 h-8 text-amber-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Conversor Pesetas ↔ Euros</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Pesetas (PTS)</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={pesetas}
                        onChange={(e) => handlePesetasChange(e.target.value)}
                        placeholder="0"
                        className="w-full px-4 py-3 text-lg border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">PTS</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <ArrowRightLeft className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>

                  <div className="space-y-2 md:col-start-2 md:row-start-1">
                    <label className="text-sm font-medium text-gray-700">Euros (EUR)</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={euros}
                        onChange={(e) => handleEurosChange(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        className="w-full px-4 py-3 text-lg border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">€</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <Button variant="outline" onClick={limpiarConversor} className="gap-2">
                    <RefreshCw className="w-4 h-4" /> Limpiar
                  </Button>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <h3 className="font-medium text-gray-900 mb-2">Ejemplos de conversión</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">1.000 PTS =</span> <span className="font-medium">6,01 €</span></div>
                    <div><span className="text-gray-500">10 € =</span> <span className="font-medium">1.664 PTS</span></div>
                    <div><span className="text-gray-500">10.000 PTS =</span> <span className="font-medium">60,10 €</span></div>
                    <div><span className="text-gray-500">100 € =</span> <span className="font-medium">16.639 PTS</span></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {!isFullscreen && (
            <div className="text-sm text-gray-500 text-center">
              Tipo de cambio irrevocable fijado por el Consejo de la UE el 31 de diciembre de 1998
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Calculadora;
